import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Field, StatusPill } from "@ozpanel/kit";
import { Button, Details, Section, StateView, Technical, When, useAuth, useToast } from "@ozpanel/ui";

// The installation's licence (O6): what the licensing service last said, signed — trial days left, expiry,
// whether updates may be installed, its notes — and where the key is entered. Read from GET /ozpanel/license,
// which re-verifies the stored answer on every read (internal/modules/licence). Nothing on this page stops a
// site or a service, in any state (O7 — the owner's decision is pending on that proposal).

export interface LicenceVerdict {
  state: "licensed" | "lapsed" | "unlicensed" | "offline" | "unknown";
  kind?: string;
  status?: string;
  expires_at?: string;
  days_left?: number;
  valid_until?: string;
  updates_allowed: boolean;
  banner?: string;
  messages?: string[] | null;
  reasons?: string[] | null;
  // Set per licence on the licensing service's admin, signed with the answer (internal/licence).
  features?: Record<string, unknown> | null;
  grace_days?: number;
  serving: boolean;
  management_locked: boolean;
  lock_at?: string;
}

// The switches the product acts on; anything else a licence carries is shown as an entitlement.
const FEATURE_WORDS: Record<string, string> = {
  updates_while_offline: "Updates continue while the licensing service is unreachable",
  updates_after_expiry: "Updates continue after the licence ends",
  lock_management_on_end: "The admin console becomes read-only when the licence ends",
  lock_grace_days: "Days before that happens",
};

export interface LicenceStatus {
  installation_id: string;
  key_set: boolean;
  key_set_at?: string;
  licence_id?: string;
  verdict: LicenceVerdict;
  answer_at?: string;
  last_try_at?: string;
  last_error?: string;
  next_check_s: number;
  service: string;
  decider: string;
  note?: string;
}

const STATE: Record<LicenceVerdict["state"], { tone: "ok" | "warn" | "bad" | "neutral"; words: string }> = {
  licensed: { tone: "ok", words: "licensed" },
  lapsed: { tone: "warn", words: "lapsed" },
  unlicensed: { tone: "warn", words: "no licence" },
  offline: { tone: "warn", words: "not confirmed" },
  unknown: { tone: "neutral", words: "waiting for the first answer" },
};

const KIND: Record<string, string> = { trial: "Trial", yearly: "Yearly", founding: "Founding", "one-time": "One-time" };

export function useLicence() {
  const { client } = useAuth();
  return useQuery({
    queryKey: ["licence"],
    queryFn: () => client.apiFetch<LicenceStatus>("/ozpanel/license"),
    refetchInterval: 10 * 60_000,
    retry: false,
  });
}

/** The licence module's panel, on its own module page (MODULE_OPS.licence) — the sidebar entry is the module's. */
export function LicencePanel() {
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const q = useLicence();
  const [key, setKey] = React.useState("");
  const [busy, setBusy] = React.useState<"" | "save" | "check">("");

  const run = async (what: "save" | "check") => {
    setBusy(what);
    try {
      const out =
        what === "save"
          ? await client.apiFetch<LicenceStatus>("/ozpanel/license", { method: "PUT", body: JSON.stringify({ key }) })
          : await client.apiFetch<LicenceStatus>("/ozpanel/license/check", { method: "POST" });
      qc.setQueryData(["licence"], out);
      if (what === "save") setKey("");
      notify({ kind: out.last_error ? "error" : "success", title: what === "save" ? "Licence key saved" : "Checked", body: out.note });
    } catch (e) {
      notify({ kind: "error", title: what === "save" ? "The key was not saved" : "The check failed", body: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy("");
    }
  };

  const d = q.data;
  const v = d?.verdict;
  return (
    <div className="space-y-4">
      <StateView loading={q.isLoading} error={q.error} errorContext="the licence">
        {d && v && (
          <>
            <Section title="This installation" actions={<StatusPill tone={STATE[v.state].tone}>{STATE[v.state].words}</StatusPill>}>
              {v.banner && <p className="mb-3 rounded-lg border border-line bg-bg2 px-3 py-2 text-[13.5px]">{v.banner}</p>}
              <dl className="grid gap-x-6 gap-y-2 text-[13.5px] sm:grid-cols-2">
                <Row label="Licence">{v.kind ? KIND[v.kind] ?? v.kind : "—"}</Row>
                <Row label="Days left">{v.days_left != null ? `${v.days_left}` : v.kind === "one-time" ? "does not expire" : "—"}</Row>
                <Row label="Expires">{v.expires_at ? <When value={v.expires_at} /> : "—"}</Row>
                <Row label="Updates">{v.updates_allowed ? "may be installed" : "not until the licence is confirmed and covers them"}</Row>
                <Row label="Last answer">{d.answer_at ? <When value={d.answer_at} /> : "none yet"}</Row>
                <Row label="Answer stands until">{v.valid_until ? <When value={v.valid_until} /> : "—"}</Row>
                <Row label="Offline grace">{v.grace_days != null ? `${v.grace_days} day(s)` : "—"}</Row>
                <Row label="Admin console">
                  {v.management_locked ? (
                    <span className="text-warn">read-only until the licence is renewed</span>
                  ) : v.lock_at ? (
                    <>
                      read-only from <When value={v.lock_at} /> unless renewed
                    </>
                  ) : (
                    "never locked by this licence"
                  )}
                </Row>
              </dl>
              {v.features && Object.keys(v.features).length > 0 && (
                <div className="mt-3">
                  <div className="mb-1 text-[12.5px] font-medium text-muted">Set on this licence</div>
                  <ul className="space-y-0.5 text-[13px]">
                    {Object.entries(v.features).map(([k, val]) => (
                      <li key={k}>
                        {FEATURE_WORDS[k] ?? k}: <span className="font-medium">{typeof val === "boolean" ? (val ? "yes" : "no") : String(val)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {(v.messages ?? []).length > 0 && (
                <ul className="mt-3 list-disc space-y-1 pl-5 text-[13px]">
                  {(v.messages ?? []).map((m) => (
                    <li key={m}>{m}</li>
                  ))}
                </ul>
              )}
              {d.last_error && (
                <p className="mt-3 text-[13px] text-warn">
                  The last check failed: {d.last_error}
                  {d.last_try_at && (
                    <>
                      {" "}
                      (<When value={d.last_try_at} />)
                    </>
                  )}
                </p>
              )}
              <p className="mt-3 text-[12.5px] text-muted">
                Sites, mail, apps and your customers' own consoles keep running whatever this page says. The most a
                licence can do is make this admin console read-only after it ends, when that is set on the licence —
                signing in, this page and recovery always work.
              </p>
              <div className="mt-3">
                <Button size="sm" variant="ghost" disabled={busy !== ""} onClick={() => void run("check")}>
                  {busy === "check" ? "Checking…" : "Check now"}
                </Button>
              </div>
              <Technical>
                <div className="mt-3 space-y-1 font-mono text-[11.5px] text-faint">
                  <div>installation {d.installation_id}</div>
                  {d.licence_id && <div>licence {d.licence_id}</div>}
                  <div>{d.service}</div>
                  <div>{d.decider}; every {Math.round(d.next_check_s / 3600)} h</div>
                  {(v.reasons ?? []).map((r) => (
                    <div key={r}>{r}</div>
                  ))}
                </div>
              </Technical>
            </Section>

            <Section title={d.key_set ? "Change the licence key" : "Enter the licence key"}>
              <form
                className="flex flex-wrap items-end gap-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (key.trim()) void run("save");
                }}
              >
                <div className="min-w-[260px] flex-1">
                  <Field label="Licence key" value={key} onChange={setKey} placeholder="OZP-XXXXX-XXXXX-XXXXX-XXXXX" mono />
                </div>
                <Button type="submit" variant="accent" disabled={!key.trim() || busy !== ""}>
                  {busy === "save" ? "Saving…" : "Save and check"}
                </Button>
              </form>
              <Details summary="Where is my key?" className="mt-3">
                It is in the email sent when you signed up or bought the licence. The key is stored encrypted and never
                shown again here; entering a new one replaces it.
                {d.key_set_at && (
                  <>
                    {" "}
                    The current one was entered <When value={d.key_set_at} />.
                  </>
                )}
              </Details>
            </Section>
          </>
        )}
      </StateView>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-2">
      <dt className="w-40 shrink-0 text-muted">{label}</dt>
      <dd className="min-w-0">{children}</dd>
    </div>
  );
}
